import { Box, Divider, Grid2, Stack, Typography } from '@mui/material';
import type { FieldArrayRenderProps, FieldProps } from 'formik';
import { Field, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Checkbox from '@components/Checkbox';
import {
  FormikDropdownField,
  FormikFhirDateField,
  FormikTextField,
} from '@components/FormikFields';
import IconButton from '@components/IconButton';
import PhoneNumbersField from '@components/PhoneNumbersField';
import QuestionnaireForm from '@components/Questionnaire/Form';
import TextInputBox from '@components/TextInputBox';

import { RoleName } from '@constants/role_name';

import useQuestionnaire from '@hooks/useQuestionnaire';

import type { OnboardingMember } from '@models/onboarding-member';

import { PlanType } from 'generated/types';

const messages = defineMessages({
  plan: {
    id: 'newAccountForm.plan',
    defaultMessage: 'Patient Plan',
  },
  firstName: {
    id: 'GLOBAL.firstName',
    defaultMessage: 'First Name',
  },
  middleName: {
    id: 'GLOBAL.middleName',
    defaultMessage: 'Middle Name',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  user: {
    id: 'newAccountForm.user',
    defaultMessage: 'User #{number}',
  },
  additionalUser: {
    id: 'newAccountForm.additionalUser',
    defaultMessage: 'Additional User #{number} Details',
  },
  remove: {
    id: 'newAccountForm.remove',
    defaultMessage: 'Remove User',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
  isPatient: {
    id: 'newAccountForm.isPatient',
    defaultMessage: 'Is this user a patient?',
  },
});

interface NewAccountFormProps {
  arrayHelpers: FieldArrayRenderProps;
  value: OnboardingMember;
  index: number;
  isAddForm?: boolean;
  /**
   * If `true`, all new members are treated as patients. If `false`, all new members are not treated as patients. Otherwise, all fields are shown.
   */
  isPatient?: boolean;
  /**
   * The parent ID (organization) the user is being added under.
   * If provided, a plan type dropdown will be rendered for each user and will be required.
   */
  planOptions?:
    | {
        k: string;
        v: string;
      }[]
    | null;
}

function getRole(planType: PlanType | null) {
  return isPatientMember(planType) ? RoleName.Patient : RoleName.Client;
}

function isPatientMember(plan: PlanType | null) {
  switch (plan) {
    case PlanType.PATIENT:
      return true;
    default:
      return false;
  }
}

const NewAccountForm: React.FC<NewAccountFormProps> = ({
  arrayHelpers,
  value,
  index = 0,
  isAddForm = false,
  isPatient,
  planOptions,
}) => {
  const form = useFormikContext();
  const { formatMessage } = useIntl();
  // Different from PATIENT and CAREGIVER plans

  const { data, formHelpers } = useQuestionnaire();
  const questionnaire = data?.getQuestionnaire.questionnaire;

  const member: OnboardingMember = value;

  React.useEffect(() => {
    let planType: PlanType | null = null;
    if (isPatient === true) {
      planType = PlanType.PATIENT;
    } else if (isPatient === undefined) {
      // Not externally controlled - preserve the existing selection on remount
      planType = member.plan ?? null;
    }

    form.setFieldValue(`members.${index}.plan`, planType);
    form.setFieldValue(`members.${index}.pricing`, planType);
    form.setFieldValue(`members.${index}.role`, getRole(planType));
    form.setFieldValue(`members.${index}.isPatient`, isPatientMember(planType));

    // Only initialize questionnaire responses when not yet set; preserve on remount
    if (
      member.questionnaireResponses === undefined ||
      member.questionnaireResponses === null
    ) {
      form.setFieldValue(
        `members.${index}.questionnaireResponses`,
        formHelpers.initialValues
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatient]);

  return (
    <Box>
      <Stack direction="row">
        <Typography variant="overline">
          {formatMessage(messages[isAddForm ? 'additionalUser' : 'user'], {
            number: index + 1,
          })}
        </Typography>
        <Box>
          {index !== 0 ? (
            <IconButton
              name="xmark"
              iconType="fas"
              onClick={() => arrayHelpers.remove(index)}
              tooltip={formatMessage(messages.remove)}
            />
          ) : null}
        </Box>
      </Stack>
      <Divider />
      <Grid2 container spacing={1}>
        <Grid2 size={{ sm: 4 }}>
          <FormikTextField
            name={`members.${index}.firstName`}
            label={formatMessage(messages.firstName)}
            charLimit={50}
            required
          />
        </Grid2>
        <Grid2 size={{ sm: 4 }}>
          <FormikTextField
            name={`members.${index}.middleName`}
            label={formatMessage(messages.middleName)}
            charLimit={50}
          />
        </Grid2>
        <Grid2 size={{ sm: 4 }}>
          <FormikTextField
            name={`members.${index}.lastName`}
            label={formatMessage(messages.lastName)}
            charLimit={50}
            required
          />
        </Grid2>

        {(member.plan && [PlanType.PATIENT].includes(member.plan)) ||
        member.plan === null ? (
          <Grid2 size={{ sm: 6 }}>
            <Field name={`members.${index}.email`}>
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.email)}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  required={
                    (index === 0 && !isAddForm) ||
                    member.plan === null ||
                    (isAddForm && member.plan !== PlanType.PATIENT)
                  }
                  width="100%"
                  type="email"
                  inputProps={{ maxLength: 50 }}
                  {...field}
                />
              )}
            </Field>
          </Grid2>
        ) : null}

        {typeof isPatient !== 'boolean' && (
          <Grid2 size={{ sm: 12 }}>
            <Field name={`members.${index}.plan`}>
              {({
                field,
                meta,
                form,
              }: FieldProps<string, { members: OnboardingMember[] }>) => (
                <Checkbox
                  checked={field.value === PlanType.PATIENT}
                  {...field}
                  error={Boolean(meta.touched ? meta.error : '')}
                  helperText={meta.touched ? meta.error : ''}
                  label={formatMessage(messages.isPatient)}
                  onChange={(event) => {
                    const planType: PlanType | null = event.target.checked
                      ? PlanType.PATIENT
                      : null;
                    const members = [...form.values.members];
                    member.plan = planType;
                    member.pricing = planType;
                    member.role = getRole(planType);
                    member.isPatient = isPatientMember(planType);
                    form.setValues({ ...form.values, members });
                  }}
                />
              )}
            </Field>
          </Grid2>
        )}

        {member.plan &&
        [PlanType.PATIENT].includes(member.plan) &&
        (typeof isPatient !== 'boolean' || isPatient === true) ? (
          <>
            <Grid2 size={{ sm: 6 }}>
              <FormikFhirDateField
                formats={['date']}
                label={formatMessage(messages.dob)}
                name={`members.${index}.dateOfBirth`}
                required
                value={(form.values as any).members[index]?.dateOfBirth}
              />
            </Grid2>
            <Grid2 size={{ sm: 6 }}>
              {planOptions?.length ? (
                <FormikDropdownField
                  label={formatMessage(messages.plan)}
                  name={`members.${index}.planId`}
                  options={planOptions}
                  required
                />
              ) : null}
            </Grid2>
            {/* Takes the full row: extra numbers stack, each with a radio. */}
            <Grid2 size={{ xs: 12 }}>
              <PhoneNumbersField
                fullWidth
                isEditing
                name={`members.${index}.phones`}
                // The phone is optional here, and the blank default row is
                // stripped on submit. `required` would let the browser block
                // submit before Formik ever runs.
                required={false}
              />
            </Grid2>
          </>
        ) : null}

        {value.isPatient ? (
          <QuestionnaireForm
            data={questionnaire}
            namePrefix={`members.${index}.questionnaireResponses.`}
          />
        ) : null}
      </Grid2>
    </Box>
  );
};

export default NewAccountForm;
